/**
 * Image Generator Types, Constants, and Helpers
 *
 * Shared types and utilities for the Image Generator arche.
 * Extracted from ImageGeneratorPage to enable reuse across hooks and components.
 */

import { ParameterDefinition } from '@/lib/api';
import { ImageGenerationStage } from '../components/ImageGenerationProgress';

// ============================================================================
// Constants
// ============================================================================

/** Number of AI instances supported */
export const NUM_INSTANCES = 5;

/** Maximum number of enhancement history items per category */
export const MAX_HISTORY_ITEMS = 10;

/** Arche ID for image-generator */
export const ARCHE_ID = 'arche_image_generator';

// localStorage key functions (scoped to userId)
export function getEnhancementHistoryStorageKey(userId: string): string {
  return `fias-image-enhancement-history-${userId}`;
}
export function getPresetsStorageKey(userId: string): string {
  return `fias-image-presets-${userId}`;
}
export function getInstanceConfigsStorageKey(userId: string): string {
  return `fias-image-instance-configs-${userId}`;
}
export function getTagDefinitionsStorageKey(userId: string): string {
  return `fias-image-tag-definitions-${userId}`;
}
export function getInstanceImagesStorageKey(userId: string): string {
  return `fias-image-instance-images-${userId}`;
}
export function getPanelLayoutStorageKey(userId: string): string {
  return `fias-image-panel-layout-${userId}`;
}

// Fallback options when model definition is not yet loaded
export const FALLBACK_SIZE_OPTIONS = [
  { value: '1:1', label: 'Square (1:1)' },
  { value: '16:9', label: 'Landscape (16:9)' },
  { value: '9:16', label: 'Portrait (9:16)' },
  { value: '4:3', label: 'Landscape (4:3)' },
  { value: '3:4', label: 'Portrait (3:4)' },
  { value: '3:2', label: 'Landscape (3:2)' },
  { value: '2:3', label: 'Portrait (2:3)' },
];

export const FALLBACK_QUALITY_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'hd', label: 'HD' },
];

export const FALLBACK_STYLE_OPTIONS = [
  { value: 'vivid', label: 'Vivid' },
  { value: 'natural', label: 'Natural' },
];

/** Prompt enhancement categories */
export const ENHANCEMENT_CATEGORIES = [
  {
    key: 'subjectDetails',
    label: 'Subject Details',
    description:
      'Physical characteristics (size, shape, color, texture), specific features (fur type, facial expression, pose), age, condition, or state of the subject.',
    placeholder: 'e.g., fluffy orange fur, sitting pose, curious expression',
  },
  {
    key: 'composition',
    label: 'Composition & Framing',
    description:
      "Camera angle (close-up, wide shot, bird's eye view), subject positioning (centered, rule of thirds), depth of field, and focus points.",
    placeholder: 'e.g., close-up shot, centered, shallow depth of field',
  },
  {
    key: 'lighting',
    label: 'Lighting',
    description:
      'Light source type (natural, studio, dramatic), direction (backlit, side-lit, soft diffused), and time of day implications (golden hour, midday).',
    placeholder: 'e.g., soft natural lighting, golden hour, backlit',
  },
  {
    key: 'styleMedium',
    label: 'Style & Medium',
    description:
      'Artistic style (photorealistic, painterly, illustration), rendering technique (3D render, watercolor, digital art), and artistic influences or era references.',
    placeholder: 'e.g., photorealistic, oil painting style, Studio Ghibli',
  },
  {
    key: 'background',
    label: 'Background & Setting',
    description:
      'Environment description (indoor, outdoor, abstract), location context (forest, city, studio backdrop), and supporting elements and props.',
    placeholder: 'e.g., cozy living room, autumn forest, minimalist backdrop',
  },
  {
    key: 'mood',
    label: 'Mood & Atmosphere',
    description:
      'Emotional tone (serene, dramatic, whimsical) and ambiance (cozy, mysterious, energetic).',
    placeholder: 'e.g., peaceful, mysterious, joyful energy',
  },
  {
    key: 'colorTone',
    label: 'Color & Tone',
    description:
      'Color palette suggestions, contrast and saturation levels, and color temperature (warm, cool).',
    placeholder: 'e.g., warm earth tones, high contrast, muted pastels',
  },
  {
    key: 'technicalQuality',
    label: 'Technical Quality',
    description:
      'Resolution/detail references ("highly detailed", "4K") and rendering quality descriptors.',
    placeholder: 'e.g., highly detailed, 4K, sharp focus',
  },
] as const;

// ============================================================================
// Provider Parameters (Engine-Specific Settings)
// ============================================================================

/** Provider params are an open record of engine-specific values */
export type ProviderParams = Record<string, unknown>;

/** Empty provider params default */
export const EMPTY_PROVIDER_PARAMS: ProviderParams = {};

/** Control type for rendering a provider param field */
export type ProviderParamControlType = 'textarea' | 'slider' | 'number' | 'checkbox' | 'select';

/** Definition for a single provider parameter */
export interface ProviderParamDefinition {
  key: string;
  label: string;
  description: string;
  controlType: ProviderParamControlType;
  /** Models that support this param (by model ID) */
  supportedModels: string[];
  /** Default value */
  defaultValue: string | number | boolean;
  /** For slider/number: minimum value */
  min?: number;
  /** For slider/number: maximum value */
  max?: number;
  /** For slider: step increment */
  step?: number;
  /** For select: dropdown options */
  options?: { value: string; label: string }[];
  /** Categorizes this param as quality or style for extraction */
  category?: 'quality' | 'style';
}

/**
 * Returns the param definitions applicable to a given model from entity capabilities.
 * Falls back to empty array if entity data is unavailable.
 */
export function getProviderParamsForModel(
  modelName: string,
  models?: { modelName: string; capabilities: Record<string, unknown> }[],
): ProviderParamDefinition[] {
  if (models) {
    const model = models.find((m) => m.modelName === modelName);
    const params = model?.capabilities?.providerParams;
    if (Array.isArray(params)) {
      return params as ProviderParamDefinition[];
    }
  }
  return [];
}

/**
 * Returns all unique provider param definitions across all models.
 * Used by PresetEditDialog to show all possible params.
 */
export function getAllProviderParamDefinitions(
  models?: { capabilities: Record<string, unknown> }[],
): ProviderParamDefinition[] {
  if (!models) return [];
  const seen = new Set<string>();
  const result: ProviderParamDefinition[] = [];
  for (const model of models) {
    const params = model.capabilities?.providerParams;
    if (Array.isArray(params)) {
      for (const param of params as ProviderParamDefinition[]) {
        if (!seen.has(param.key)) {
          seen.add(param.key);
          result.push(param);
        }
      }
    }
  }
  return result;
}

/** Returns true if the model has any engine-specific parameters */
export function modelHasProviderParams(
  modelName: string,
  models?: { modelName: string; capabilities: Record<string, unknown> }[],
): boolean {
  return getProviderParamsForModel(modelName, models).length > 0;
}

/**
 * Maps between common pixel dimensions and aspect ratios so size validation
 * accepts both formats. Mirrors `image-generate.ts` on the backend.
 */
const PIXEL_TO_RATIO: Record<string, string> = {
  '1024x1024': '1:1',
  '1792x1024': '16:9',
  '1024x1792': '9:16',
  '1536x1024': '16:9',
  '1024x1536': '9:16',
  '1920x1080': '16:9',
  '1080x1920': '9:16',
  '1365x1024': '4:3',
  '1024x1365': '3:4',
  '1280x1024': '5:4',
  '1024x1280': '4:5',
  '1820x1024': '16:9',
  '1024x1820': '9:16',
  '2048x2048': '1:1',
};

const RATIO_TO_COMMON_PIXEL: Record<string, string> = {
  '1:1': '1024x1024',
  '16:9': '1792x1024',
  '9:16': '1024x1792',
  '4:3': '1365x1024',
  '3:4': '1024x1365',
  '3:2': '1536x1024',
  '2:3': '1024x1536',
  '5:4': '1280x1024',
  '4:5': '1024x1280',
};

/**
 * Returns the supportedSizes declared by the given model's capabilities,
 * or undefined if the model has no constraint (in which case any size is allowed).
 */
export function getSupportedSizesForModel(
  modelName: string,
  models?: { modelName: string; capabilities?: Record<string, unknown> | null }[],
): string[] | undefined {
  if (!models) return undefined;
  const model = models.find((m) => m.modelName === modelName);
  const sizes = model?.capabilities?.supportedSizes;
  if (Array.isArray(sizes) && sizes.length > 0) {
    return sizes as string[];
  }
  return undefined;
}

/**
 * Returns true if `size` is compatible with the model's supportedSizes,
 * normalizing between aspect-ratio and pixel-dimension formats so a `1:1`
 * size matches a `2048x2048` supported entry and vice versa.
 *
 * If the model declares no supportedSizes, every size is considered valid.
 */
export function isSizeSupportedByModel(
  size: string,
  modelName: string,
  models?: { modelName: string; capabilities?: Record<string, unknown> | null }[],
): boolean {
  const supported = getSupportedSizesForModel(modelName, models);
  if (!supported) return true;
  if (supported.includes(size)) return true;
  const asRatio = PIXEL_TO_RATIO[size];
  if (asRatio && supported.includes(asRatio)) return true;
  const asPixel = RATIO_TO_COMMON_PIXEL[size];
  if (asPixel && supported.includes(asPixel)) return true;
  return false;
}

/**
 * Picks a safe fallback size for the given model when the user's current size
 * is incompatible. Prefers `1:1` (or its pixel equivalent), otherwise returns
 * the first entry from the model's supportedSizes. Returns null when the model
 * declares no constraints — in which case the existing size should be kept.
 */
export function pickDefaultSizeForModel(
  modelName: string,
  models: { modelName: string; capabilities?: Record<string, unknown> | null }[],
): string | null {
  const supported = getSupportedSizesForModel(modelName, models);
  if (!supported) return null;
  if (supported.includes('1:1')) return '1:1';
  if (supported.includes('1024x1024')) return '1024x1024';
  if (supported.includes('2048x2048')) return '2048x2048';
  return supported[0];
}

/** Filters a providerParams record to only include params supported by the given model */
export function filterProviderParamsForModel(
  providerParams: ProviderParams,
  modelName: string,
  models?: { modelName: string; capabilities: Record<string, unknown> }[],
): ProviderParams {
  const applicableKeys = new Set(
    getProviderParamsForModel(modelName, models).map((def) => def.key),
  );
  const filtered: ProviderParams = {};
  for (const [key, value] of Object.entries(providerParams)) {
    if (applicableKeys.has(key) && value !== '' && value !== undefined) {
      filtered[key] = value;
    }
  }
  return filtered;
}

/** Legacy quality param keys — used when entity data lacks `category` field */
const LEGACY_QUALITY_PARAM_KEYS = ['dalle_quality', 'gpt_quality'] as const;
/** Legacy style param keys — used when entity data lacks `category` field */
const LEGACY_STYLE_PARAM_KEYS = ['dalle_style', 'recraft_style', 'stability_style'] as const;

/**
 * Extracts quality and style from providerParams (where they live as engine params),
 * returning them as top-level API fields and the remaining providerParams.
 * Uses entity `category` field first, falls back to legacy key lists for pre-migration entities.
 * Falls back to config.quality/config.style for backward compatibility with old presets.
 */
export function extractQualityStyleFromParams(
  filteredParams: ProviderParams,
  config: { quality: string; style: string },
  paramDefs?: ProviderParamDefinition[],
): { quality: string; style: string; remainingParams: ProviderParams } {
  const remaining: ProviderParams = {};
  let quality = config.quality;
  let style = config.style;

  // Build category lookup from entity param definitions
  const categoryByKey = new Map<string, 'quality' | 'style'>();
  if (paramDefs) {
    for (const def of paramDefs) {
      if (def.category) categoryByKey.set(def.key, def.category);
    }
  }

  for (const [key, value] of Object.entries(filteredParams)) {
    const category = categoryByKey.get(key);
    if (category === 'quality') {
      if (value && value !== '' && value !== 'none') quality = String(value);
    } else if (category === 'style') {
      if (value && value !== '' && value !== 'none') style = String(value);
    } else if (!category && (LEGACY_QUALITY_PARAM_KEYS as readonly string[]).includes(key)) {
      // Fallback for pre-migration entities without category field
      if (value && value !== '' && value !== 'none') quality = String(value);
    } else if (!category && (LEGACY_STYLE_PARAM_KEYS as readonly string[]).includes(key)) {
      if (value && value !== '' && value !== 'none') style = String(value);
    } else {
      remaining[key] = value;
    }
  }

  return { quality, style, remainingParams: remaining };
}

// ============================================================================
// Types
// ============================================================================

export type EnhancementKey = (typeof ENHANCEMENT_CATEGORIES)[number]['key'];

/** Generated image data */
export interface GeneratedImage {
  requestId: string;
  imageRef: string;
  imageUrl: string;
  prompt: string;
  revisedPrompt?: string;
  provider: string;
  model: string;
  size: string;
  quality: string;
  style: string;
  enhancements: Record<EnhancementKey, string>;
  generatedAt: Date;
  instanceId?: number;
  /** Whether a reference image was used */
  usedReferenceImage?: boolean;
  /** Creativity level used (0-100%, only when reference image was used) */
  creativityPercent?: number;
  /** Credits this image actually charged (from the completion event); 1 credit = 1¢. */
  costCredits?: number;
}

/** Image settings preset */
export interface ImagePreset {
  id: string;
  name: string;
  size: string;
  quality: string;
  style: string;
  enhancements: Record<EnhancementKey, string>;
  providerParams: ProviderParams;
  /** When set, the preset includes a specific model */
  modelName?: string;
  /** Provider for the saved model */
  provider?: string;
  createdAt: string;
  tags: string[];
  categoryId: string | null;
}

/** Tag definition for organizing presets */
export interface TagDefinition {
  id: string;
  name: string;
}

/** Default strength for reference images (0 = ignore reference, 1 = copy reference) */
export const DEFAULT_REFERENCE_STRENGTH = 0.5;

/** Reference image for image-to-image generation */
export interface ReferenceImage {
  /** S3 key for images from generation or upload */
  imageRef?: string;
  /** File ID for images selected from the Fias file system */
  fileId?: string;
  thumbnailUrl: string;
  source: 'generated' | 'uploaded' | 'file-system';
  /** How closely to follow the reference (0 = ignore, 1 = copy). Default 0.5 */
  strength?: number;
}

/** Per-instance configuration */
export interface ImageInstanceConfig {
  instanceId: number;
  presetName: string;
  originalPresetId?: string | null;
  /** Whether to include the model when saving presets */
  includeModelInPreset: boolean;
  modelName: string;
  provider: string;
  size: string;
  quality: string;
  style: string;
  enhancements: Record<EnhancementKey, string>;
  providerParams: ProviderParams;
  /** Reference image for image-to-image generation */
  referenceImage?: ReferenceImage | null;
}

/** Per-instance state */
export interface ImageInstanceState {
  stage: ImageGenerationStage;
  progressMessage: string;
  currentRequestId: string | null;
  error: string | null;
}

// ============================================================================
// Factory Functions
// ============================================================================

/** Default empty enhancements */
export const EMPTY_ENHANCEMENTS = ENHANCEMENT_CATEGORIES.reduce(
  (acc, cat) => ({ ...acc, [cat.key]: '' }),
  {} as Record<EnhancementKey, string>,
);

/**
 * Popular image-generation defaults, one per panel, so the three panels start
 * on different providers instead of all pointing at the same model.
 */
const DEFAULT_MODELS_BY_INSTANCE: Array<{ modelName: string; provider: string }> = [
  { modelName: 'gpt-image-1', provider: 'openai' },
  { modelName: 'flux-1.1-pro', provider: 'bfl' },
  { modelName: 'stable-image-ultra', provider: 'stability' },
];

/**
 * Creates default instance config. Each instance gets a different popular
 * default model (see `DEFAULT_MODELS_BY_INSTANCE`). If a model list is
 * supplied, prefer the per-instance default when it is in the list, otherwise
 * fall back to the first available model.
 */
export function createDefaultInstanceConfig(
  instanceId: number,
  models?: { modelName: string; provider: string }[],
): ImageInstanceConfig {
  const fallback = DEFAULT_MODELS_BY_INSTANCE[instanceId] ?? DEFAULT_MODELS_BY_INSTANCE[0];
  const fromList =
    models?.find((m) => m.modelName === fallback.modelName) ??
    (models && models.length > 0 ? models[0] : null);
  return {
    instanceId,
    presetName: 'Default Preset',
    includeModelInPreset: false,
    modelName: fromList?.modelName || fallback.modelName,
    provider: fromList?.provider || fallback.provider,
    size: '1:1',
    quality: 'standard',
    style: 'vivid',
    enhancements: { ...EMPTY_ENHANCEMENTS },
    providerParams: {},
    referenceImage: null,
  };
}

/** Creates default instance state */
export function createDefaultInstanceState(): ImageInstanceState {
  return {
    stage: 'idle',
    progressMessage: '',
    currentRequestId: null,
    error: null,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/** Suffix appended to a preset name when the user modifies settings */
const UNSAVED_SUFFIX = ' (Unsaved)';

/** Returns true if the preset name indicates unsaved modifications */
export function isUnsavedPresetName(name: string): boolean {
  return name.endsWith(UNSAVED_SUFFIX);
}

/** Appends "(Unsaved)" to a preset name if not already present */
export function toUnsavedPresetName(name: string): string {
  return isUnsavedPresetName(name) ? name : name + UNSAVED_SUFFIX;
}

/** Strips the "(Unsaved)" suffix to recover the original preset name */
export function toBasePresetName(name: string): string {
  return isUnsavedPresetName(name) ? name.slice(0, -UNSAVED_SUFFIX.length) : name;
}

/** Extracts options from a parameter schema for a specific parameter name */
export function getOptionsFromSchema(
  parameterSchema: ParameterDefinition[],
  paramName: string,
  fallback: { value: string; label: string }[],
): { value: string; label: string }[] {
  const param = parameterSchema.find((p) => p.name === paramName);
  if (param?.options && param.options.length > 0) {
    return param.options.map((opt) => ({
      value: opt.value,
      label: opt.label,
    }));
  }
  return fallback;
}

/**
 * Format model name for display using entity data.
 * Accepts an optional models array to look up the entity displayName.
 * Falls back to the raw modelName if no match is found.
 */
export function formatModelName(
  modelName: string,
  models?: { modelName: string; displayName: string }[],
): string {
  if (models) {
    const match = models.find((m) => m.modelName === modelName);
    if (match) return match.displayName;
  }
  return modelName;
}

/** Model info for the info popover */
export interface ImageModelInfo {
  company: string;
  description: string;
  strengths?: string;
}

const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  openai: 'OpenAI',
  google: 'Google',
  bfl: 'FLUX (Black Forest Labs)',
  recraft: 'Recraft',
  stability: 'Stability AI',
};

/**
 * Extracts ImageModelInfo from an entity's capabilities.
 * Falls back to provider name and shortDescription when enriched data is missing.
 */
export function getImageModelInfo(
  modelName: string,
  models?: {
    modelName: string;
    shortDescription: string;
    provider?: string;
    capabilities: Record<string, unknown>;
  }[],
): ImageModelInfo | null {
  if (!models) return null;
  const model = models.find((m) => m.modelName === modelName);
  if (!model) return null;
  const caps = model.capabilities;
  const company =
    (caps?.company as string) || PROVIDER_DISPLAY_NAMES[model.provider ?? ''] || model.provider;
  if (!company) return null;
  return {
    company,
    description: model.shortDescription,
    strengths: (caps?.strengths as string) || undefined,
  };
}

/** Get numeric aspect ratio value for CSS aspect-ratio property (e.g. "16/9") */
export function getAspectRatioValue(size: string): string {
  // Handle ratio strings like "16:9"
  const ratioMatch = size.match(/^(\d+):(\d+)$/);
  if (ratioMatch) return `${ratioMatch[1]}/${ratioMatch[2]}`;

  // Handle pixel dimensions like "1024x768"
  const pxMatch = size.match(/^(\d+)x(\d+)$/);
  if (pxMatch) return `${pxMatch[1]}/${pxMatch[2]}`;

  return '1/1';
}

/** Get aspect ratio class based on image size string */
export function getAspectRatioClass(size: string): string {
  // Handle aspect ratio strings
  if (size === '16:9') return 'aspect-video';
  if (size === '9:16') return 'aspect-[9/16]';
  if (size === '4:3') return 'aspect-[4/3]';
  if (size === '3:4') return 'aspect-[3/4]';
  if (size === '3:2') return 'aspect-[3/2]';
  if (size === '2:3') return 'aspect-[2/3]';
  if (size === '5:4') return 'aspect-[5/4]';
  if (size === '4:5') return 'aspect-[4/5]';
  if (size === '21:9') return 'aspect-[21/9]';
  if (size === '9:21') return 'aspect-[9/21]';

  // Handle pixel dimensions (WxH)
  const match = size.match(/^(\d+)x(\d+)$/);
  if (match) {
    const w = parseInt(match[1], 10);
    const h = parseInt(match[2], 10);
    if (w === h) return 'aspect-square';
    return `aspect-[${w}/${h}]`;
  }

  return 'aspect-square';
}

/** Default estimated duration when entity data is unavailable */
const DEFAULT_ESTIMATED_DURATION_MS = 18000;

/**
 * Returns the estimated generation duration in milliseconds for a given model.
 * Reads from entity capabilities; falls back to a sensible default.
 */
export function getEstimatedDurationMs(
  modelName: string,
  models?: { modelName: string; capabilities: Record<string, unknown> }[],
): number {
  if (models) {
    const model = models.find((m) => m.modelName === modelName);
    const duration = model?.capabilities?.estimatedDurationMs;
    if (typeof duration === 'number' && duration > 0) return duration;
  }
  return DEFAULT_ESTIMATED_DURATION_MS;
}

/** Models known to support reference images (fallback when entity data is unavailable) */
const MODELS_WITH_REFERENCE_SUPPORT = new Set([
  'gpt-image-1.5',
  'sd3.5-large',
  'sd3.5-large-turbo',
  'sd3.5-medium',
  'stable-image-ultra',
  'stable-image-core',
  'flux-2-pro',
  'flux-1.1-pro',
  'flux-1.1-pro-ultra',
  'flux-2-flex',
  'flux-2-klein-9b',
  'gemini-2.5-flash-image',
]);

/**
 * Returns whether a model supports reference images for image-to-image generation.
 * Checks entity capabilities first, falls back to a known model list.
 */
export function modelSupportsReferenceImage(
  modelName: string,
  models?: { modelName: string; capabilities: Record<string, unknown> }[],
): boolean {
  if (models) {
    const model = models.find((m) => m.modelName === modelName);
    if (model?.capabilities?.supportsReferenceImage !== undefined) {
      return Boolean(model.capabilities.supportsReferenceImage);
    }
  }
  return MODELS_WITH_REFERENCE_SUPPORT.has(modelName);
}
